import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { CRM_MAX_CACHE_WINDOW_DAYS } from '../../constants/crmConnector';

interface CrmConnectorCacheWindowDialogProps {
  open: boolean;
  accountName: string;
  currentDays?: number | null;
  onClose: () => void;
  onSave: (days: number) => Promise<void>;
}

export default function CrmConnectorCacheWindowDialog({
  open,
  accountName,
  currentDays,
  onClose,
  onSave,
}: CrmConnectorCacheWindowDialogProps) {
  const [daysInput, setDaysInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDaysInput(String(currentDays ?? CRM_MAX_CACHE_WINDOW_DAYS));
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentDays]);

  const daysInvalid = !/^[1-9]\d*$/.test(daysInput.trim()) || Number(daysInput) > CRM_MAX_CACHE_WINDOW_DAYS;

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onSave(Number(daysInput.trim()));
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save the cache window');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cache window</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
          How many days of payments to keep cached for <strong>{accountName}</strong>.
        </Box>
        <TextField
          fullWidth
          type="number"
          label="Cache window (days)"
          value={daysInput}
          onChange={(e) => setDaysInput(e.target.value)}
          disabled={submitting}
          error={daysInvalid}
          helperText={daysInvalid ? `Enter a whole number of days from 1 to ${CRM_MAX_CACHE_WINDOW_DAYS}.` : undefined}
          inputProps={{ min: 1, max: CRM_MAX_CACHE_WINDOW_DAYS, step: 1 }}
        />
        <Box sx={{ mt: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>
          Widening the window imports the newly covered days. Narrowing it leaves cached data in place.
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || daysInvalid}
          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
